import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { reportService } from '../../services/reportService';
import type { MonthlyReport, DetailedMonthlyReport, ReportReservation, ReportProductPurchase } from '../../types/report';

type ReportMode = 'summary' | 'detailed';

interface MonthlyReportModalProps {
  businessProfileId: string;
  onClose: () => void;
}

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export function MonthlyReportModal({ businessProfileId, onClose }: MonthlyReportModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [mode, setMode] = useState<ReportMode>('summary');
  const [report, setReport] = useState<MonthlyReport | DetailedMonthlyReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const monthNames = [
    t('months.january', 'Styczeń'),
    t('months.february', 'Luty'),
    t('months.march', 'Marzec'),
    t('months.april', 'Kwiecień'),
    t('months.may', 'Maj'),
    t('months.june', 'Czerwiec'),
    t('months.july', 'Lipiec'),
    t('months.august', 'Sierpień'),
    t('months.september', 'Wrzesień'),
    t('months.october', 'Październik'),
    t('months.november', 'Listopad'),
    t('months.december', 'Grudzień'),
  ];

  const availableYears = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear()];

  useEffect(() => {
    if (businessProfileId && token) {
      fetchReport();
    }
  }, [selectedYear, selectedMonth, mode, businessProfileId, token]);

  const fetchReport = async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);
    setReport(null);

    try {
      if (mode === 'detailed') {
        const data = await reportService.getMonthlyReportDetailed(businessProfileId, selectedYear, selectedMonth, token);
        setReport(data);
      } else {
        const data = await reportService.getMonthlyReport(businessProfileId, selectedYear, selectedMonth, token);
        setReport(data);
      }
    } catch (err) {
      setError(t('reports.error', 'Nie udało się pobrać raportu'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => `${amount.toFixed(2)} zł`;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getReservationStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return t('reports.statusActive', 'Aktywna');
      case 'cancelled': return t('reports.statusCancelled', 'Anulowana');
      case 'completed': return t('reports.statusCompleted', 'Zakończona');
      default: return status;
    }
  };

  const getReservationStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'text-brand-confirm-deep bg-brand-confirm/30';
      case 'cancelled': return 'text-brand-warm-deep bg-brand-warm/30';
      default: return 'text-ink-secondary bg-surface-app-elevated';
    }
  };

  const isDetailed = (r: MonthlyReport | DetailedMonthlyReport): r is DetailedMonthlyReport =>
    'reservations' in r;

  const escapeCsv = (val: unknown): string => {
    const str = val == null ? '' : String(val);
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const generateCSV = (r: MonthlyReport | DetailedMonthlyReport): string => {
    const rows: string[] = [];

    rows.push(`${t('reports.monthlyReport', 'Raport miesięczny')} - ${r.monthName}`);
    rows.push('');

    rows.push(t('reports.revenue', 'Przychody'));
    rows.push([t('reports.totalRevenue', 'Przychód całkowity'), r.totalRevenue.toFixed(2)].map(escapeCsv).join(','));
    rows.push([t('reports.reservationRevenue', 'Przychód z rezerwacji'), r.reservationRevenue.toFixed(2)].map(escapeCsv).join(','));
    rows.push([t('reports.productRevenue', 'Przychód z usług'), r.productPurchaseRevenue.toFixed(2)].map(escapeCsv).join(','));
    rows.push([t('reports.refundedAmount', 'Zwroty'), r.refundedAmount.toFixed(2)].map(escapeCsv).join(','));
    rows.push([t('reports.netRevenue', 'Przychód netto'), r.netRevenue.toFixed(2)].map(escapeCsv).join(','));
    rows.push('');

    rows.push(t('reports.reservations', 'Rezerwacje'));
    rows.push([t('reports.totalReservations', 'Liczba rezerwacji'), r.totalReservations].map(escapeCsv).join(','));
    rows.push([t('reports.cancelledReservations', 'Anulowane'), r.cancelledReservations].map(escapeCsv).join(','));
    rows.push([t('reports.productPurchases', 'Zakupy usług'), r.productPurchaseCount].map(escapeCsv).join(','));
    rows.push('');

    if (r.facilityBreakdown?.length) {
      rows.push(t('reports.facilityBreakdown', 'Podział na obiekty'));
      rows.push(['Obiekt', 'Przychód', 'Rezerwacje', 'Anulowane'].map(escapeCsv).join(','));
      r.facilityBreakdown.forEach(f => {
        rows.push([f.facilityName, f.revenue.toFixed(2), f.reservationCount, f.cancelledCount].map(escapeCsv).join(','));
      });
      rows.push('');
    }

    if (isDetailed(r)) {
      rows.push(t('reports.reservationsList', 'Lista rezerwacji'));
      rows.push(['ID', 'Data', 'Obiekt', 'Klient', 'Email', 'Cena', 'Status', 'Użytkownicy', 'Online', 'Usługa'].map(escapeCsv).join(','));
      r.reservations.forEach((res: ReportReservation) => {
        rows.push([
          res.id,
          formatDate(res.date),
          res.facilityName,
          res.customerName ?? '',
          res.customerEmail ?? '',
          res.totalPrice.toFixed(2),
          res.status,
          res.numberOfUsers,
          res.paidOnline ? 'Tak' : 'Nie',
          res.paidWithProduct ? 'Tak' : 'Nie',
        ].map(escapeCsv).join(','));
      });
      rows.push('');

      rows.push(t('reports.productPurchasesList', 'Lista zakupów usług'));
      rows.push(['ID', 'Data zakupu', 'Usługa', 'Klient', 'Cena', 'Status', 'Pozostałe użycia', 'Wygasa'].map(escapeCsv).join(','));
      r.productPurchases.forEach((p: ReportProductPurchase) => {
        rows.push([
          p.id,
          formatDate(p.purchaseDate),
          p.productTitle,
          p.customerName,
          (p.grossPrice ?? p.price).toFixed(2),
          p.status,
          `${p.remainingUsage}/${p.totalUsage}`,
          formatDate(p.expiryDate),
        ].map(escapeCsv).join(','));
      });
    }

    return rows.join('\n');
  };

  const handleDownloadCSV = () => {
    if (!report) return;

    setIsDownloading(true);
    setShowDownloadMenu(false);

    try {
      const csv = generateCSV(report);
      const filename = `raport_${report.year}_${String(report.month).padStart(2, '0')}.csv`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('error', t('reports.downloadError', 'Nie udało się pobrać raportu'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-strong">
          <h2 className="text-lg font-semibold text-white">
            {t('reports.monthlyReport', 'Raport miesięczny')}
          </h2>
          <div className="flex items-center gap-2">
            {report && (
              <div className="relative">
                <button
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  disabled={isDownloading}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white"
                >
                  {isDownloading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                </button>
                {showDownloadMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-surface-card border border-border-strong rounded-lg shadow-lg overflow-hidden z-10">
                    <button
                      onClick={handleDownloadCSV}
                      className="w-full px-4 py-2 text-left text-sm text-ink-primary hover:bg-surface-card-hover transition-colors"
                    >
                      {t('reports.downloadCSV', 'Pobierz CSV')}
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="p-4 border-b border-border-strong space-y-3">
          {/* Mode Toggle */}
          <div className="flex bg-white/10 rounded-lg p-1">
            <button
              onClick={() => setMode('summary')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${mode === 'summary'
                ? 'bg-brand-primary text-white'
                : 'text-white/70 hover:text-white'
                }`}
            >
              {t('reports.modeSummary', 'Podsumowanie')}
            </button>
            <button
              onClick={() => setMode('detailed')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${mode === 'detailed'
                ? 'bg-brand-primary text-white'
                : 'text-white/70 hover:text-white'
                }`}
            >
              {t('reports.modeDetailed', 'Szczegółowy')}
            </button>
          </div>

          {/* Period Selectors */}
          <div className="flex gap-3">
            {/* Year Selector */}
            <div className="relative flex-1">
              <button
                onClick={() => { setShowYearPicker(!showYearPicker); setShowMonthPicker(false); }}
                className="w-full flex items-center justify-between px-3 py-2 bg-surface-card dark:bg-white/10 border border-border-strong rounded-lg hover:bg-surface-card-hover dark:hover:bg-white/15 transition-colors"
              >
                <div className="text-left">
                  <span className="text-xs text-ink-muted">{t('reports.year', 'Rok')}</span>
                  <p className="text-sm text-ink-primary font-medium">{selectedYear}</p>
                </div>
                <svg className="w-4 h-4 text-ink-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showYearPicker && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-surface-card border border-border-strong rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  {availableYears.map(year => (
                    <button
                      key={year}
                      onClick={() => { setSelectedYear(year); setShowYearPicker(false); }}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${year === selectedYear
                        ? 'bg-brand-primary text-white'
                        : 'text-ink-primary hover:bg-surface-card-hover'
                        }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Month Selector */}
            <div className="relative flex-1">
              <button
                onClick={() => { setShowMonthPicker(!showMonthPicker); setShowYearPicker(false); }}
                className="w-full flex items-center justify-between px-3 py-2 bg-surface-card dark:bg-white/10 border border-border-strong rounded-lg hover:bg-surface-card-hover dark:hover:bg-white/15 transition-colors"
              >
                <div className="text-left">
                  <span className="text-xs text-ink-muted">{t('reports.month', 'Miesiąc')}</span>
                  <p className="text-sm text-ink-primary font-medium">{monthNames[selectedMonth - 1]}</p>
                </div>
                <svg className="w-4 h-4 text-ink-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showMonthPicker && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-surface-card border border-border-strong rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  {MONTHS.map(m => (
                    <button
                      key={m}
                      onClick={() => { setSelectedMonth(m); setShowMonthPicker(false); }}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${m === selectedMonth
                        ? 'bg-brand-primary text-white'
                        : 'text-ink-primary hover:bg-surface-card-hover'
                        }`}
                    >
                      {monthNames[m - 1]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="w-8 h-8 animate-spin text-brand-primary" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-sm text-ink-secondary mt-3">{t('reports.loading', 'Ładowanie raportu...')}</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-sm text-brand-warm-deep mb-4">{error}</p>
              <button
                onClick={fetchReport}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors text-sm"
              >
                {t('common.retry', 'Spróbuj ponownie')}
              </button>
            </div>
          ) : report ? (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white">{report.monthName}</h3>

              {/* Revenue Card */}
              <div className="bg-surface-card dark:bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-brand-confirm-deep" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <span className="font-medium text-ink-primary">{t('reports.revenue', 'Przychody')}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-secondary">{t('reports.totalRevenue', 'Przychód całkowity')}</span>
                    <span className="text-lg font-bold text-brand-primary">{formatCurrency(report.totalRevenue)}</span>
                  </div>
                  <div className="h-px bg-surface-app-elevated" />
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-secondary">{t('reports.reservationRevenue', 'Z rezerwacji')}</span>
                    <span className="text-sm text-ink-primary">{formatCurrency(report.reservationRevenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-ink-secondary">{t('reports.productRevenue', 'Z usług')}</span>
                    <span className="text-sm text-ink-primary">{formatCurrency(report.productPurchaseRevenue)}</span>
                  </div>
                  {report.refundedAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm text-ink-secondary">{t('reports.refundedAmount', 'Zwroty')}</span>
                      <span className="text-sm text-brand-warm-deep">-{formatCurrency(report.refundedAmount)}</span>
                    </div>
                  )}
                  <div className="h-px bg-surface-app-elevated" />
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-ink-primary">{t('reports.netRevenue', 'Przychód netto')}</span>
                    <span className="text-lg font-bold text-brand-primary">{formatCurrency(report.netRevenue)}</span>
                  </div>
                </div>
              </div>

              {/* Reservations Summary */}
              <div className="bg-surface-card dark:bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium text-ink-primary">{t('reports.reservations', 'Rezerwacje')}</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-brand-primary">{report.totalReservations}</p>
                    <p className="text-xs text-ink-secondary">{t('reports.total', 'Wszystkie')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-brand-warm-deep">{report.cancelledReservations}</p>
                    <p className="text-xs text-ink-secondary">{t('reports.cancelled', 'Anulowane')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-brand-confirm-deep">{report.productPurchaseCount}</p>
                    <p className="text-xs text-ink-secondary">{t('reports.products', 'Usługi')}</p>
                  </div>
                </div>
              </div>

              {/* Facility Breakdown */}
              {report.facilityBreakdown && report.facilityBreakdown.length > 0 && (
                <div className="bg-surface-card dark:bg-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span className="font-medium text-ink-primary">{t('reports.facilityBreakdown', 'Podział na obiekty')}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-ink-secondary text-left">
                          <th className="pb-2">{t('reports.facility', 'Obiekt')}</th>
                          <th className="pb-2 text-right">{t('reports.revenue', 'Przychód')}</th>
                          <th className="pb-2 text-center">{t('reports.reservations', 'Rezerwacje')}</th>
                          <th className="pb-2 text-center">{t('reports.cancelled', 'Anulowane')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.facilityBreakdown.map((facility, index) => (
                          <tr key={facility.facilityId} className={index % 2 === 1 ? 'bg-surface-app-elevated' : ''}>
                            <td className="py-2 text-ink-primary">{facility.facilityName}</td>
                            <td className="py-2 text-right font-medium text-brand-primary">{formatCurrency(facility.revenue)}</td>
                            <td className="py-2 text-center text-ink-primary">{facility.reservationCount}</td>
                            <td className="py-2 text-center text-brand-warm-deep">{facility.cancelledCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detailed: Reservations List */}
              {isDetailed(report) && (
                <div className="bg-surface-card dark:bg-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span className="font-medium text-ink-primary">{t('reports.reservationsList', 'Lista rezerwacji')}</span>
                    </div>
                    <span className="text-xs text-ink-secondary bg-surface-app-elevated px-2 py-1 rounded">{report.reservations.length}</span>
                  </div>

                  {report.reservations.length === 0 ? (
                    <p className="text-sm text-ink-muted text-center py-4">{t('reports.noReservations', 'Brak rezerwacji')}</p>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {report.reservations.map((res: ReportReservation) => (
                        <div key={res.id} className="bg-surface-app-elevated rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-surface-app-elevated text-ink-primary px-2 py-1 rounded">{formatDate(res.date)}</span>
                              <span className="text-sm font-medium text-ink-primary truncate">{res.facilityName}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${getReservationStatusColor(res.status)}`}>
                              {getReservationStatusLabel(res.status)}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 mb-2">
                            {res.timeSlots.map(slot => (
                              <span key={slot} className="text-xs bg-brand-primary/15 text-brand-primary px-2 py-0.5 rounded">{slot}</span>
                            ))}
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <div>
                              <p className="text-ink-primary">{res.customerName}</p>
                              <p className="text-xs text-ink-muted">{res.customerEmail}</p>
                            </div>
                            <p className="font-bold text-brand-primary">{formatCurrency(res.totalPrice)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Detailed: Product Purchases List */}
              {isDetailed(report) && (
                <div className="bg-surface-card dark:bg-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      <span className="font-medium text-ink-primary">{t('reports.productPurchasesList', 'Lista zakupów usług')}</span>
                    </div>
                    <span className="text-xs text-ink-secondary bg-surface-app-elevated px-2 py-1 rounded">{report.productPurchases.length}</span>
                  </div>

                  {report.productPurchases.length === 0 ? (
                    <p className="text-sm text-ink-muted text-center py-4">{t('reports.noProductPurchases', 'Brak zakupów usług')}</p>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {report.productPurchases.map((purchase: ReportProductPurchase) => (
                        <div key={purchase.id} className="bg-surface-app-elevated rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-ink-primary truncate">{purchase.productTitle}</span>
                            <span className="text-sm font-bold text-brand-primary">{formatCurrency(purchase.grossPrice ?? purchase.price)}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-ink-secondary">
                            <span>{purchase.customerName}</span>
                            <span>{t('reports.usage', 'Użycia')}: {purchase.remainingUsage}/{purchase.totalUsage}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-ink-muted mt-1">
                            <span>{t('reports.purchased', 'Zakupiono')}: {formatDate(purchase.purchaseDate)}</span>
                            <span>{t('reports.expires', 'Wygasa')}: {formatDate(purchase.expiryDate)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
